import React from 'react';
import { H1, P } from '../../components/elements/Text';
import Navbar from '../../components/Navbar';
import PageWithAsyncData from '../../components/PageWithAsyncData';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useGetCompany } from '../../hooks/use-hydra/admin';
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  CompanyResponse,
  assignMentorToCompany,
  createMentor,
} from '../../api/admin';
import {
  TableHead,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  ClickableTableRow,
} from '../../components/Table';
import { Table } from '../../components/Table';
import { TableWrapper } from '../../components/Table';
import { toast } from 'react-toastify';
import Button from '../../components/elements/Button';
import day from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Dropdown } from '../../components/elements/Dropdown';
import { FormLabel, FormInput } from '../../components/Form';
import { useMutation } from 'react-query';
import useAuth from '../../hooks/use-auth';
import { COMPANY_PAGES } from '../../components/Admin/CompanySideNavigation';
import CompanyPageWrapper from '../../components/Admin/CompanyPageWrapper';
import { ROUTES } from '../../routing';
day.extend(utc);

const Header = ({ name, id }: { name?: string; id?: string }) => (
  <Navbar
    breadCrumbs={[
      {
        label: 'Home',
        url: ROUTES.HOME.getPath(),
      },
      {
        label: 'Teams',
        url: ROUTES.ADMIN.COMPANIES.getPath(),
      },
      ...(name && id
        ? [
            {
              label: name,
              url: ROUTES.ADMIN.COMPANY.getPath({ companyId: id }),
            },
          ]
        : []),
      { label: 'Mentors', url: null },
    ]}
  />
);

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0px;
  margin-bottom: 72px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: baseline;
  width: 70%;
  margin-top: 16px;
`;

const DropdownContainer = styled.div`
  flex: 1;

  div {
    margin: 0;
  }
`;

const InputContainer = styled.div`
  flex: 1;
`;

const AddMentorSection = styled.div`
  margin-bottom: 32px;
`;

const StyledButton = styled(Button)`
  margin: 0;
  min-width: 200px;
`;

type MentorOption = {
  value: string;
  label: string;
  id: number;
  name: string;
  email: string;
};

export default function Mentors() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error, refetch } = useGetCompany(parseInt(id));
  const { getTokenSilently } = useAuth();

  // State for creating new mentors
  const [newMentorName, setNewMentorName] = React.useState('');
  const [newMentorEmail, setNewMentorEmail] = React.useState('');

  // State for assigning existing mentors
  const [selectedMentor, setSelectedMentor] =
    React.useState<MentorOption | null>(null);

  const companyMentors = (data?.mentors ?? []).filter((m) => m.isCompanyMentor);
  const nonCompanyMentors = (data?.mentors ?? []).filter(
    (m) => !m.isCompanyMentor
  );

  // Convert non-company mentors to dropdown options
  const mentorOptions: MentorOption[] = nonCompanyMentors.map((mentor) => ({
    value: `existing-${mentor.id}`,
    label: `${mentor.name} (${mentor.email})`,
    id: mentor.id,
    name: mentor.name,
    email: mentor.email,
  }));

  const createMentorMutation = useMutation({
    mutationFn: async () => {
      if (!newMentorName.trim()) {
        throw new Error('Please provide a name for the new mentor');
      }

      if (!newMentorEmail.trim()) {
        throw new Error('Please provide an email for the new mentor');
      }

      // Basic email validation
      if (!newMentorEmail.includes('@') || !newMentorEmail.includes('.')) {
        throw new Error('Please provide a valid email address');
      }

      const token = await getTokenSilently();

      await createMentor(token, {
        name: newMentorName.trim(),
        email: newMentorEmail.trim(),
      });
    },
    onSuccess: async () => {
      await refetch();
      toast.success(`Successfully created mentor ${newMentorName}`);
      setNewMentorName('');
      setNewMentorEmail('');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create mentor');
    },
  });

  const assignMentorMutation = useMutation({
    mutationFn: async () => {
      if (!selectedMentor) {
        throw new Error('No mentor selected');
      }

      const token = await getTokenSilently();

      await assignMentorToCompany(token, {
        companyId: parseInt(id),
        mentorProfileId: selectedMentor.id,
      });
    },
    onSuccess: async () => {
      await refetch();
      toast.success(
        `Successfully assigned ${selectedMentor?.name} to the company`
      );
      setSelectedMentor(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to assign mentor to company');
    },
  });

  return (
    <PageWithAsyncData isLoading={isLoading} error={error} retry={refetch}>
      <Header name={data?.name} id={id} />
      <CompanyPageWrapper currentPage={COMPANY_PAGES.MENTORS}>
        {' '}
        <H1>{data?.name} Mentors</H1>
        <Content>
          <AddMentorSection>
            <FormLabel>Create New Mentors</FormLabel>
            <Row>
              <InputContainer>
                <FormInput
                  placeholder="Enter new mentor name.."
                  value={newMentorName}
                  onChange={(e) => setNewMentorName(e.target.value)}
                  isDisabled={createMentorMutation.isLoading}
                />
              </InputContainer>
              <InputContainer>
                <FormInput
                  placeholder="Enter new mentor email..."
                  type="email"
                  value={newMentorEmail}
                  onChange={(e) => setNewMentorEmail(e.target.value)}
                  isDisabled={createMentorMutation.isLoading}
                />
              </InputContainer>
              <StyledButton
                label="Create Mentor"
                type="primary"
                faIcon="fa-solid fa-plus"
                showIconBeforeLabel
                isDisabled={
                  !newMentorName.trim() ||
                  !newMentorEmail.trim() ||
                  createMentorMutation.isLoading
                }
                isLoading={createMentorMutation.isLoading}
                onClick={() => createMentorMutation.mutate()}
              />
            </Row>
          </AddMentorSection>

          <AddMentorSection>
            <FormLabel>Assign Existing Mentors to Company</FormLabel>
            <Row>
              <DropdownContainer>
                <Dropdown
                  isValidNewOption={() => false}
                  options={mentorOptions}
                  value={selectedMentor}
                  onChange={(option: MentorOption | null) =>
                    setSelectedMentor(option)
                  }
                  placeholder="Select a mentor to assign to the company..."
                  noOptionsMessage={() => 'No available mentors found'}
                  isDisabled={assignMentorMutation.isLoading}
                />
              </DropdownContainer>
              <StyledButton
                label="Assign Mentor to Company"
                type="primary"
                faIcon="fa-solid fa-user-plus"
                showIconBeforeLabel
                isDisabled={!selectedMentor || assignMentorMutation.isLoading}
                isLoading={assignMentorMutation.isLoading}
                onClick={() => assignMentorMutation.mutate()}
              />
            </Row>
          </AddMentorSection>

          <P>
            There are {companyMentors?.length} mentors assigned to this company.
          </P>
          <MentorsTable data={companyMentors} />
        </Content>
      </CompanyPageWrapper>
    </PageWithAsyncData>
  );
}

const columnHelper = createColumnHelper<CompanyResponse['mentors'][number]>();

const columns = [
  columnHelper.accessor('id', {
    header: 'ID',
    meta: {
      styles: {
        width: '80px',
      },
    },
  }),
  columnHelper.accessor('name', {
    header: 'Name',
    cell: ({ getValue }) => getValue() ?? 'N/A',
    meta: {
      styles: {
        minWidth: '300px',
        maxWidth: '300px',
      },
    },
  }),
  columnHelper.accessor('email', {
    header: 'Email',
    cell: ({ getValue }) => getValue() ?? 'N/A',
    meta: {
      styles: {
        minWidth: '250px',
        width: '100%',
        maxWidth: '400px',
      },
    },
  }),
];

const Wrapper = styled(TableWrapper)`
  overflow-x: auto;

  table {
    min-width: 600px;
  }
`;

const MentorsTable = ({ data }: { data: CompanyResponse['mentors'] }) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id.toString(),
  });

  if (data.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead meta={header.column.columnDef.meta} key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <ClickableTableRow
              key={row.id}
              onClick={() => {
                toast.info('Mentor profiles not implemented.');
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell meta={cell.column.columnDef.meta} key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </ClickableTableRow>
          ))}
        </TableBody>
      </Table>
    </Wrapper>
  );
};
